<template>
  <div class="section">
    <div class="">
      <!-- Header -->
      <div class="level mb-5">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title is-3">Orders Dashboard</h1>
              
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <button class="button is-light is-small" @click="clearFilters">
              <span>Reset Filters</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="orders_loading" class="box has-text-centered">
        <progress class="progress is-large is-info" max="100">Loading...</progress>
      </div>

      <!-- Filters -->
      <div v-else class="box mb-5">
        <p class="heading has-text-grey mb-3">Filters</p>
        <div class="columns is-multiline">
          <div class="column is-4">
            <label class="label is-small">Range</label>
            <div class="tags">
              <span
                v-for="range in uniqueRanges"
                :key="range"
                class="tag is-medium is-clickable"
                :class="filterRanges.includes(range) ? 'is-info' : 'is-light'"
                @click="toggleFilter('filterRanges', range)"
              >
                {{ range }}
              </span>
            </div>
          </div>

          <div class="column is-4">
            <label class="label is-small">Order Type</label>
            <div class="tags">
              <span
                v-for="order_type in uniqueorder_types"
                :key="order_type"
                class="tag is-medium is-clickable"
                :class="filterorder_types.includes(order_type) ? 'is-info' : 'is-light'"
                @click="toggleFilter('filterorder_types', order_type)"
              >
                {{ order_type }}
              </span>
            </div>
          </div>

          <div class="column is-4">
            <label class="label is-small">Makeup Location</label>
            <div class="tags">
              <span
                v-for="location in uniqueLocations"
                :key="location"
                class="tag is-medium is-clickable"
                :class="filterLocations.includes(location) ? 'is-info' : 'is-light'"
                @click="toggleFilter('filterLocations', location)"
              >
                {{ location }}
              </span>
            </div>
          </div>

          <div class="column is-4">
            <p class="label is-small">Print Location</p>
            <div class="tags">
              <span
                v-for="location in uniquePrintLocations"
                :key="location"
                class="tag is-medium is-clickable"
                :class="filterPrintLocations.includes(location) ? 'is-info' : 'is-light'"
                @click="toggleFilter('filterPrintLocations', location)"
              >
                {{ location }}
              </span>
            </div>
          </div>
        </div>
        <p class="help has-text-grey">
          Showing <strong>{{ filteredOrders.length }}</strong> of <strong>{{ orders.length }}</strong> orders
          <span v-if="activeFilterCount > 0"> — {{ activeFilterCount }} filter{{ activeFilterCount > 1 ? 's' : '' }} active</span>
        </p>
      </div>

      <!-- Summary Cards -->
      <div v-if="!orders_loading" class="columns is-multiline mb-5">
        <div class="column is-2">
          <div class="box has-background-info-light">
            <p class="heading">Total Orders</p>
            <p class="title">{{ totalOrders }}</p>
          </div>
        </div>
        <div class="column is-2">
          <div class="box has-background-success-light">
            <p class="heading">Garments</p>
            <p class="title">{{ totalQty }}</p>
          </div>
        </div>
        <div class="column is-2">
          <div class="box has-background-warning-light">
            <p class="heading">Total Printed</p>
            <p class="title">{{ totalqty_printed }}</p>
          </div>
        </div>
        <div class="column is-2">
          <div class="box has-background-danger-light">
            <p class="heading">Total Trans</p>
            <p class="title">{{ totalqty_rolls }}</p>
          </div>
        </div>
        <div class="column is-2">
          <div class="box has-background-primary-light">
            <p class="heading">Total Cut</p>
            <p class="title">{{ totalqty_cut }}</p>
          </div>
        </div>
      </div>

      <!-- Orders Table -->
      <div v-if="!orders_loading" class="box mt-5">
        <div class="tabs is-toggle is-small mb-3">
          <ul>
            <li :class="{ 'is-active': activeTableTab === 'orders' }">
              <a @click.prevent="activeTableTab = 'orders'">Orders</a>
            </li>
            <li :class="{ 'is-active': activeTableTab === 'region' }">
              <a @click.prevent="activeTableTab = 'region'">By Region</a>
            </li>
          </ul>
        </div>

        <h2 v-if="activeTableTab === 'orders'" class="subtitle">Orders List</h2>
        <h2 v-else class="subtitle">Region Breakdown</h2>

        <div v-if="activeTableTab === 'orders'" class="table-container">
          <table class="table is-fullwidth is-striped is-hoverable is-size-7">
            <thead>
              <tr class="is-selected">
                <th>PO Number</th>
                <th>Client Name</th>
                <th>Region</th>
                <th>Range</th>
                <th>Order Type</th>
                <th>Order Date</th>
                <th>Print Location</th>
                <th>Makeup Location</th>
                <th>Total</th>
                <th>Printed</th>
                <th>Trans</th>
                <th>Cut</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in filteredOrders" :key="order.ponumber">
                <td>
                  <span class="tag is-light">{{ order.ponumber }}</span>
                </td>
                <td>{{ order.clientname }}</td>
                <td>{{ order.Region || order.region }}</td>
                <td>{{ order.range }}</td>
                <td>{{ order.order_type }}</td>
                <td>{{ formatDate(order.orderdate) }}</td>
                <td>{{ order.printlocation }}</td>
                <td>{{ order.makeuplocation }}</td>
                <td>
                  <span class="tag is-info">{{ order.total_qty }}</span>
                </td>
                <td>
                  <span
                    :class="{
                      'tag is-success': order.qty_printed === order.total_qty,
                      'tag is-warning': order.qty_printed < order.total_qty,
                    }"
                  >
                    {{ order.qty_printed }}
                  </span>
                </td>
                <td>
                  <span
                    :class="{
                      'tag is-success': order.qty_rolls === order.total_qty,
                      'tag is-warning': order.qty_rolls < order.total_qty,
                    }"
                  >
                    {{ order.qty_rolls }}
                  </span>
                </td>
                <td>
                  <span
                    :class="{
                      'tag is-success': order.qty_cut === order.total_qty,
                      'tag is-warning': order.qty_cut < order.total_qty,
                    }"
                  >
                    {{ order.qty_cut }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="table-container">
          <table class="table is-fullwidth is-striped is-hoverable is-size-7">
            <thead>
              <tr class="is-selected">
                <th>Region</th>
                <th>Orders</th>
                <th>Total</th>
                <th>Printed</th>
                <th>Trans</th>
                <th>Cut</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in regionBreakdown" :key="row.region">
                <td>{{ row.region }}</td>
                <td>{{ row.orders }}</td>
                <td><span class="tag is-info">{{ row.total_qty }}</span></td>
                <td>{{ row.qty_printed }}</td>
                <td>{{ row.qty_rolls }}</td>
                <td>{{ row.qty_cut }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="activeTableTab === 'orders' && filteredOrders.length === 0" class="has-text-centered mt-5">
          <p class="subtitle is-5">No orders found matching your filters.</p>
        </div>
        <div v-if="activeTableTab === 'region' && regionBreakdown.length === 0" class="has-text-centered mt-5">
          <p class="subtitle is-5">No region totals available.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrderService from '@/services/OrderService.js';

export default {
  name: 'Overview',
  data() {
    return {
      orders: [
        {
          ponumber: 'PO-001',
          clientname: 'Acme Corp',
          range: 'Jerseys',
          order_type: 'Standard',
          orderdate: '2026-03-15',
          makeuplocation: 'Warehouse A',
          printlocation: 'Printer 1',
          total_qty: 500,
          qty_printed: 500,
          qty_rolls: 480,
          qty_cut: 470,
        },
        {
          ponumber: 'PO-002',
          clientname: 'Tech Solutions',
          range: 'Leisurewear',
          order_type: 'Rush',
          orderdate: '2026-03-18',
          makeuplocation: 'Warehouse B',
          printlocation: 'Printer 2',
          total_qty: 1000,
          qty_printed: 850,
          qty_rolls: 820,
          qty_cut: 790,
        },
        {
          ponumber: 'PO-003',
          clientname: 'Global Traders',
          range: 'Socks',
          order_type: 'Standard',
          orderdate: '2026-03-20',
          makeuplocation: 'Warehouse A',
          printlocation: 'Printer 1',
          total_qty: 750,
          qty_printed: 750,
          qty_rolls: 735,
          qty_cut: 730,
        },
        {
          ponumber: 'PO-004',
          clientname: 'Acme Corp',
          range: 'Shorts',
          order_type: 'Express',
          orderdate: '2026-03-22',
          makeuplocation: 'Warehouse C',
          printlocation: 'Printer 3',
          total_qty: 250,
          qty_printed: 200,
          qty_rolls: 190,
          qty_cut: 185,
        },
      ],
      orders_loading: false,
      accessToken: null,
      client_id: null,
      range: null,
      start_date: null,
      end_date: null,
      filterRanges: [],
      filterorder_types: [],
      filterLocations: [],
      filterPrintLocations: [],
      activeTableTab: 'orders',
    };
  },
  created() {
    this.getAccessToken().then(() => {
      this.getOrders();
    });
  },
  methods: {
    async getAccessToken() {
      this.accessToken = await this.$auth.getTokenSilently();
    },
    async getOrders() {
      this.orders_loading = true;
      OrderService.getAggregatedOpenOrders(
        this.accessToken,
       
      ).then((orders) => {
        this.$set(this, 'orders', orders);
        this.orders_loading = false;
      });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    clearFilters() {
      this.filterRanges = [];
      this.filterorder_types = [];
      this.filterLocations = [];
      this.filterPrintLocations = [];
    },
    toggleFilter(filterKey, value) {
      const idx = this[filterKey].indexOf(value);
      if (idx === -1) {
        this[filterKey].push(value);
      } else {
        this[filterKey].splice(idx, 1);
      }
    },
  },
  computed: {
    uniqueRanges() {
      return [...new Set(this.orders.map((order) => order.range))].sort();
    },
    uniqueorder_types() {
      return [...new Set(this.orders.map((order) => order.order_type))].sort();
    },
    uniqueLocations() {
      return [...new Set(this.orders.map((order) => order.makeuplocation))].sort();
    },
    uniquePrintLocations() {
      return [...new Set(this.orders.map((order) => order.printlocation))].sort();
    },
    activeFilterCount() {
      return this.filterRanges.length + this.filterorder_types.length + this.filterLocations.length + this.filterPrintLocations.length;
    },
    totalOrders() {
      return this.filteredOrders.length;
    },
    totalQty() {
      return this.filteredOrders.reduce((sum, order) => sum + order.total_qty, 0);
    },
    totalqty_printed() {
      return this.filteredOrders.reduce((sum, order) => sum + order.qty_printed, 0);
    },
    totalqty_rolls() {
      return this.filteredOrders.reduce((sum, order) => sum + (order.qty_rolls || 0), 0);
    },
    totalqty_cut() {
      return this.filteredOrders.reduce((sum, order) => sum + (order.qty_cut || 0), 0);
    },
    regionBreakdown() {
      const grouped = this.filteredOrders.reduce((acc, order) => {
        const region = order.Region || order.region || 'Unknown';

        if (!acc[region]) {
          acc[region] = {
            region,
            orders: 0,
            total_qty: 0,
            qty_printed: 0,
            qty_rolls: 0,
            qty_cut: 0,
          };
        }

        acc[region].orders += 1;
        acc[region].total_qty += order.total_qty || 0;
        acc[region].qty_printed += order.qty_printed || 0;
        acc[region].qty_rolls += order.qty_rolls || 0;
        acc[region].qty_cut += order.qty_cut || 0;

        return acc;
      }, {});

      return Object.values(grouped).sort((a, b) => a.region.localeCompare(b.region));
    },
    filteredOrders() {
      return this.orders.filter((order) => {
        const rangeMatch =
          this.filterRanges.length === 0 ||
          this.filterRanges.includes(order.range);
        const order_typeMatch =
          this.filterorder_types.length === 0 ||
          this.filterorder_types.includes(order.order_type);
        const locationMatch =
          this.filterLocations.length === 0 ||
          this.filterLocations.includes(order.makeuplocation);
        const printLocationMatch =
          this.filterPrintLocations.length === 0 ||
          this.filterPrintLocations.includes(order.printlocation);

        return rangeMatch && order_typeMatch && locationMatch && printLocationMatch;
      });
    },
  },
  mounted() {
    // Component mounted
  },
};
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}

.section {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.tag.is-clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.tag.is-clickable:hover {
  transform: scale(1.05);
}
</style>